import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { apolloClient } from '../../lib/apolloClient';
import { gql } from '@apollo/client';
import { createContact } from '../../api/remiApi';
import { formatPhoneNumber } from '../../utils/formatters';

const GET_CALL_TESTERS = gql`
  query GetCallTesters($organizationId: String!, $limit: Int!, $offset: Int!, $sortOrder: String!) {
    contacts(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      sortOrder: $sortOrder
      isTestContact: true
    ) {
      contacts {
        contactId
        firstName
        lastName
        phone
      }
    }
  }
`;

interface CallTesterOption {
  label: string;
  value: string;
  phoneNumber: string;
}

interface AddCallTesterInput {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

const fetchCallTesters = async (organizationId: string): Promise<CallTesterOption[]> => {
  const { data } = await apolloClient.query<{ contacts?: { contacts?: { contactId: string; firstName: string; lastName: string; phone: string }[] } }>({
    query: GET_CALL_TESTERS,
    variables: { organizationId, limit: 500, offset: 0, sortOrder: 'ASC' },
    fetchPolicy: 'network-only',
  });

  return (data?.contacts?.contacts ?? [])
    .filter((c: { firstName?: string; lastName?: string; phone?: string }) =>
      c.firstName && c.lastName && c.phone
    )
    .map((c: { contactId: string; firstName: string; lastName: string; phone: string }) => ({
      label: `${c.firstName} ${c.lastName} (${formatPhoneNumber(c.phone)})`,
      value: c.contactId,
      phoneNumber: c.phone,
    }));
};

const createCallTester = async (
  organizationId: string,
  input: AddCallTesterInput
): Promise<string> => {
  const now = new Date().toISOString();
  const response = await createContact({
    organization_id: organizationId,
    firstName: input.firstName,
    lastName: input.lastName,
    phoneNumber: input.phoneNumber,
    call_tester: true,
    opt_in: true,
    opt_in_date: now.split('T')[0],
    opt_in_desc: 'Test contact — created from flow testing',
    tags: ['test-contact'],
  });
  return response?.contactId ?? response?.contact_id ?? '';
};

export const useCallTesters = (enabled = false) => {
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();

  const {
    data: callTesters = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['contacts', 'callTesters', organization?.id],
    queryFn: () => fetchCallTesters(organization?.id || ''),
    enabled: enabled && !!organization?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });

  const addCallTesterMutation = useMutation({
    mutationFn: (input: AddCallTesterInput) => createCallTester(organization?.id || '', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'callTesters', organization?.id] });
    },
  });

  return {
    callTesters,
    loading,
    error,
    refetch,
    addCallTester: (input: AddCallTesterInput) => addCallTesterMutation.mutateAsync(input),
    isAddingCallTester: addCallTesterMutation.isPending,
  };
};
